import { useEffect, useRef, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Select, DatePicker, message } from "antd";
import { LoadingOutlined } from "@ant-design/icons";
import moment from "moment/moment";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faXmark } from "@fortawesome/free-solid-svg-icons";
import {
  faPenToSquare,
  faFileLines,
  faFilePdf,
  faFileWord,
} from "@fortawesome/free-regular-svg-icons";
import dayjs from "dayjs";

import BookingTable from "../components/BookingsTable";
import { fetch as fetchBookings } from "../store/actions/booking-actions";
import { fetch as fetchDrivers } from "../store/actions/drivers-actions";
import { fetch as fetchVehicles } from "../store/actions/vehicles-actions";
import { buildBookingRef, getOptionLabel } from "../utils/helpers";
import bookingsAPI from "../API/endpoints/bookings";
import BookingDetailsModal from "../components/modals/BookingDetailsModal";
import classes from "../components/BookingsTable.module.scss";
import CancelBookingModal from "../components/modals/CancelBookingModal";
import EditBookingModal from "../components/modals/EditBookingModal";
const { RangePicker } = DatePicker;

export default function ManageBookings() {
  const [filters, setFilters] = useState({});
  const [dropdownVisible, setDropdownVisible] = useState({});
  const [isBookingDetailsOpen, setIsBookingDetailsOpen] = useState();
  const [selectedBooking, setSelectedBooking] = useState();
  const [exportingPDFs, setExportingPDFs] = useState([]);
  const [exportingWords, setExportingWords] = useState([]);
  const [isCancelModalOpen, setIsCancelModalOpen] = useState();
  const [isEditModalOpen, setIsEditModalOpen] = useState();
  const [messageApi, contextHolder] = message.useMessage();

  const dispatch = useDispatch();
  const driverSelectRef = useRef(null);
  const vehicleSelectRef = useRef(null);
  const pdfUrlRef = useRef();

  useEffect(() => {
    dispatch(fetchDrivers());
    dispatch(fetchVehicles());
  }, [dispatch]);

  useEffect(() => {
    dispatch(fetchBookings(filters));
  }, [dispatch, filters]);

  const handleFilterchange = (key, value) => {
    setFilters((prev) => {
      if (!value) {
        const { [key]: _, ...rest } = prev;
        return rest;
      }
      const updatedValues = { ...prev, [key]: value };
      if (key !== "page") {
        updatedValues.page = 0;
      }
      return updatedValues;
    });
    const property = key === "startDate" ? "date" : key;
    setDropdownVisible((prev) => ({ ...prev, [property]: false }));
  };

  const focusSelect = (ref) => {
    if (ref.current) {
      setTimeout(() => {
        ref.current?.focus();
      }, 100);
    }
  };

  const setOptionValue = (options, key) => {
    return {
      label: options.filter((u) => u.value === filters[key])[0]?.label,
      value: filters[key],
    };
  };

  const toggleBookingDetailsModal = (booking = null) => {
    if (booking) setSelectedBooking(booking);
    setIsBookingDetailsOpen((prev) => !prev);
  };

  const handleExportPDF = async (id) => {
    try {
      setExportingPDFs((prev) => {
        const newPDFs = [...prev];
        newPDFs.push(id);
        return newPDFs;
      });
      const response = await bookingsAPI.exportPDF(id);
      const blob = new Blob([response.data], {
        type: "application/pdf",
      });

      if (pdfUrlRef.current) {
        window.URL.revokeObjectURL(pdfUrlRef.current); // Clean the previous blob from memory
      }

      const newUrl = window.URL.createObjectURL(blob);
      pdfUrlRef.current = newUrl;

      window.open(newUrl, "_blank");
    } catch (error) {
      messageApi.open({
        type: "error",
        content: error.response?.data?.message || "Something went wrong",
      });
    } finally {
      setExportingPDFs((prev) => prev.filter((i) => i !== id));
    }
  };

  const handleExportWord = async (id) => {
    try {
      setExportingWords((prev) => {
        const newWords = [...prev];
        newWords.push(id);
        return newWords;
      });
      const response = await bookingsAPI.exportWord(id);

      const url = window.URL.createObjectURL(new Blob([response.data]));

      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `Delivery_Note_${buildBookingRef(id)}.docx`
      );
      document.body.appendChild(link);
      link.click();

      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      messageApi.open({
        type: "error",
        content: error.response?.data?.message || "Something went wrong",
      });
    } finally {
      setExportingWords((prev) => prev.filter((i) => i !== id));
    }
  };

  const handleTableChange = (e) => {
    const newPage = e.current - 1;
    const newSize = e.pageSize;
    const oldPage = filters.page || 0;
    const oldSize = filters.size || 10;

    if (newPage !== oldPage) {
      handleFilterchange("page", newPage);
    }
    if (newSize !== oldSize) {
      handleFilterchange("size", newSize);
    }
  };

  const isPastBookingDay = ({ date, time }) => {
    const bookingTime = moment(
      `${date} ${time}`,
      "YYYY-MM-DD HH:mm:ss"
    ).toDate();
    return moment(bookingTime).isBefore(now);
  };

  const toggleCancelModal = (booking = null) => {
    if (booking) {
      if (isPastBookingDay(booking)) return;
      setSelectedBooking(booking);
    }
    setIsCancelModalOpen((prev) => !prev);
  };

  const toggleEditModal = (booking = null) => {
    if (booking) {
      if (isPastBookingDay(booking)) return;
      setSelectedBooking(booking);
    }
    setIsEditModalOpen((prev) => !prev);
  };

  const handleBookingEdited = () => {
    messageApi.open({
      type: "success",
      content: "Booking has been edited successfully",
    });
    toggleEditModal();
  };

  const now = moment();

  const drivers = useSelector((state) => state.drivers.items);
  const driverOptions = drivers.map((d) => ({
    label: `${d.name} ${d.surname}`,
    value: d.id,
  }));

  const vehicles = useSelector((state) => state.vehicles.items);
  const vehicleOptions = vehicles.map((v) => ({
    label: `${v.licensePlate}, ${v.type}`,
    value: v.id,
  }));

  const deliveryZoneOptions = useSelector(
    (state) => state.options.deliveryZoneOptions
  );
  const deliveryTypeOptions = useSelector(
    (state) => state.options.deliveryTypeOptions
  );

  const columns = [
    {
      title: "Booking Ref",
      dataIndex: "id",
      key: "id",
      width: 100,
      render: (id) => buildBookingRef(id),
    },
    {
      title: "Driver",
      dataIndex: "driver",
      key: "driver",
      width: 200,
      filterDropdownOpen: dropdownVisible.driver,
      render: (driver) => `${driver.name} ${driver.surname}`,
      filteredValue: `${filters?.driver || ""}`,
      filterDropdown: () => (
        <Select
          ref={driverSelectRef}
          allowClear
          showSearch
          options={driverOptions}
          style={{
            width: 180,
          }}
          filterOption={(input, option) =>
            option?.label.toLowerCase().includes(input.toLowerCase())
          }
          defaultOpen={true}
          onChange={(val) => handleFilterchange("driver", val)}
          value={setOptionValue(driverOptions, "driver")}
        />
      ),
      onFilterDropdownOpenChange: (visible) => {
        if (visible) {
          setDropdownVisible((prev) => ({ ...prev, driver: true }));
          focusSelect(driverSelectRef);
        } else {
          setDropdownVisible((prev) => ({ ...prev, driver: false }));
        }
      },
    },
    {
      title: "Vehicle",
      dataIndex: "vehicle",
      key: "vehicle",
      width: 220,
      filterDropdownOpen: dropdownVisible.vehicle,
      render: (vehicle) => `${vehicle.licensePlate}, ${vehicle.type}`,
      filteredValue: `${filters?.vehicle || ""}`,
      filterDropdown: () => (
        <Select
          ref={vehicleSelectRef}
          allowClear
          showSearch
          options={vehicleOptions}
          style={{
            width: 220,
          }}
          filterOption={(input, option) =>
            option?.label.toLowerCase().includes(input.toLowerCase())
          }
          defaultOpen={true}
          onChange={(val) => handleFilterchange("vehicle", val)}
          value={setOptionValue(vehicleOptions, "vehicle")}
        />
      ),
      onFilterDropdownOpenChange: (visible) => {
        if (visible) {
          setDropdownVisible((prev) => ({ ...prev, vehicle: true }));
          focusSelect(vehicleSelectRef);
        } else {
          setDropdownVisible((prev) => ({ ...prev, vehicle: false }));
        }
      },
    },
    {
      title: "Delivery Zone",
      dataIndex: "deliveryZone",
      key: "deliveryZone",
      filterDropdownOpen: dropdownVisible.deliveryZone,
      render: (zone) => getOptionLabel(deliveryZoneOptions, zone),
      filteredValue: filters.deliveryZone,
      filterDropdown: () => (
        <Select
          allowClear
          style={{
            width: 200,
          }}
          defaultOpen={true}
          options={deliveryZoneOptions}
          onChange={(val) => handleFilterchange("deliveryZone", val)}
          value={filters?.deliveryZone || ""}
        />
      ),
      onFilterDropdownOpenChange: (visible) => {
        if (visible) {
          setDropdownVisible((prev) => ({ ...prev, deliveryZone: true }));
        } else {
          setDropdownVisible((prev) => ({ ...prev, deliveryZone: false }));
        }
      },
    },
    {
      title: "Delivery Type",
      dataIndex: "deliveryType",
      key: "deliveryType",
      filterDropdownOpen: dropdownVisible.deliveryType,
      render: (type) => getOptionLabel(deliveryTypeOptions, type),
      filteredValue: filters.deliveryType,
      filterDropdown: () => (
        <Select
          allowClear
          style={{
            width: 200,
          }}
          defaultOpen={true}
          options={deliveryTypeOptions}
          onChange={(val) => handleFilterchange("deliveryType", val)}
          value={filters?.deliveryType || ""}
        />
      ),
      onFilterDropdownOpenChange: (visible) => {
        if (visible) {
          setDropdownVisible((prev) => ({ ...prev, deliveryType: true }));
        } else {
          setDropdownVisible((prev) => ({ ...prev, deliveryType: false }));
        }
      },
    },
    {
      title: "Date",
      dataIndex: "date",
      key: "date",
      filterDropdownOpen: dropdownVisible.date,
      render: (_, record) =>
        `${dayjs(record.date).format("DD.MM.YYYY")} - ${record.time.slice(
          0,
          5
        )}`,
      filteredValue: filters.startDate,
      filterDropdown: () => (
        <RangePicker
          style={{ width: 220 }}
          onChange={(date) => {
            handleFilterchange(
              "startDate",
              dayjs(date[0]).format("YYYY-MM-DD")
            );
            handleFilterchange("endDate", dayjs(date[1]).format("YYYY-MM-DD"));
          }}
          format={"DD.MM.YYYY"}
          value={
            filters?.startDate
              ? [
                  dayjs(filters.startDate, "YYYY-MM-DD"),
                  dayjs(filters.endDate, "YYYY-MM-DD"),
                ]
              : ""
          }
        />
      ),
      onFilterDropdownOpenChange: (visible) => {
        if (visible) {
          setDropdownVisible((prev) => ({ ...prev, date: true }));
        } else {
          setDropdownVisible((prev) => ({ ...prev, date: false }));
        }
      },
    },
    {
      title: "Details",
      dataIndex: "details",
      key: "details",
      width: 84,
      align: "center",
      render: (_, record) => (
        <div style={{ display: "flex", justifyContent: "space-around" }}>
          <FontAwesomeIcon
            icon={faFileLines}
            color="#f5ba05"
            size="lg"
            onClick={() => toggleBookingDetailsModal(record)}
          />
          <>
            {!exportingPDFs.includes(record.id) ? (
              <FontAwesomeIcon
                onClick={() => handleExportPDF(record.id)}
                icon={faFilePdf}
                color="#f88"
                size="lg"
              />
            ) : (
              <LoadingOutlined disabled />
            )}
            {!exportingWords.includes(record.id) ? (
              <FontAwesomeIcon
                onClick={() => handleExportWord(record.id)}
                icon={faFileWord}
                color="#2b579a"
                size="lg"
              />
            ) : (
              <LoadingOutlined disabled />
            )}
          </>
        </div>
      ),
    },
    {
      title: "Edit",
      dataIndex: "edit",
      key: "edit",
      width: 64,
      align: "center",
      render: (_, record) => (
        <FontAwesomeIcon
          className={isPastBookingDay(record) ? classes.disabled : ""}
          onClick={() => toggleEditModal(record)}
          icon={faPenToSquare}
          color="#016871"
          size="lg"
        />
      ),
    },
    {
      title: "Cancel",
      dataIndex: "cancel",
      key: "cancel",
      width: 64,
      align: "center",
      render: (_, record) => (
        <FontAwesomeIcon
          className={isPastBookingDay(record) ? classes.disabled : ""}
          onClick={() => {
            toggleCancelModal(record);
          }}
          icon={faXmark}
          color="#f00"
          size="lg"
        />
      ),
    },
  ];

  const { items: bookings, isLoading } = useSelector((state) => state.bookings);
  const data =
    bookings?.map((booking, index) => ({
      ...booking,
      key: index,
    })) || [];

  return (
    <>
      {contextHolder}
      <BookingTable
        columns={columns}
        data={data}
        filters={filters}
        loading={isLoading}
        onClearFilters={() => setFilters({})}
        onTableChange={handleTableChange}
      />
      <BookingDetailsModal
        open={isBookingDetailsOpen}
        onClose={toggleBookingDetailsModal}
        booking={selectedBooking}
        onExportPDF={handleExportPDF}
        onExportWord={handleExportWord}
        isExportingPDF={exportingPDFs}
        isExportingWord={exportingWords}
      />
      <CancelBookingModal
        open={isCancelModalOpen}
        onClose={toggleCancelModal}
        booking={selectedBooking}
      />
      <EditBookingModal
        open={isEditModalOpen}
        onClose={toggleEditModal}
        booking={selectedBooking}
        onBookingEdited={handleBookingEdited}
      />
    </>
  );
}
